<script setup lang="ts">
/**
 * Global gem-indikator (Charlie session 49). Slide-up i footeren med en
 * indeterminate loading-bar + tekst ("Gemmer data …"), efterfulgt af et kort
 * "Gemt"- eller fejl-tail. Drevet af den reaktive saving-store, som den globale
 * fetch-interceptor (lib/saving.ts) fylder ved enhver muterende API-kald.
 *
 * Monteres ÉN gang i App.vue. Bruger design-tokens (D-026): monokrom 'active'
 * til selve baren, green/red til status-tail.
 */
import { computed } from 'vue';

import { savingState } from '../lib/saving';

const visible = computed(() => savingState.status !== 'idle');
const isSaving = computed(() => savingState.status === 'saving');
const isDone = computed(() => savingState.status === 'done');
const isError = computed(() => savingState.status === 'error');

const pct = computed(() => Math.round(savingState.percent));

const text = computed(() => {
  if (isError.value) return savingState.errorMsg || savingState.errorLabel || 'Kunne ikke gemme';
  if (isDone.value) return savingState.doneLabel || 'Gemt';
  return `${savingState.label || 'Gemmer data …'} ${pct.value}%`;
});
</script>

<template>
  <Teleport to="body">
    <Transition name="slide-up">
      <div
        v-if="visible"
        class="saving-root fixed bottom-[calc(var(--mobile-nav-h)+1.25rem)] left-1/2 z-[200] w-[min(92vw,22rem)]"
        role="status"
        aria-live="polite"
      >
        <div class="card-surface overflow-hidden rounded-xl border border-line shadow-lg">
          <!-- Loading-/status-bar: determinate fill drevet af estimeret procent -->
          <div class="h-1 w-full overflow-hidden bg-subtle">
            <div
              class="progress-fill h-full rounded-r-full"
              :class="isError ? 'bg-red' : isDone ? 'bg-green' : 'bg-active'"
              :style="{ width: pct + '%' }"
            />
          </div>

          <!-- Tekst-række -->
          <div class="flex items-center gap-2.5 px-3.5 py-2.5">
            <span v-if="isSaving" class="spinner" aria-hidden="true" />
            <svg
              v-else-if="isDone"
              class="h-4 w-4 flex-none text-green"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 9.7a1 1 0 1 1 1.4-1.4l3.1 3.1 6.8-6.8a1 1 0 0 1 1.4 0Z"
                clip-rule="evenodd"
              />
            </svg>
            <svg
              v-else
              class="h-4 w-4 flex-none text-red"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M10 1.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17ZM9 6a1 1 0 1 1 2 0v4a1 1 0 1 1-2 0V6Zm1 7a1.2 1.2 0 1 0 0 2.4 1.2 1.2 0 0 0 0-2.4Z"
                clip-rule="evenodd"
              />
            </svg>
            <span class="truncate text-xs font-medium text-ink">{{ text }}</span>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/* Centrering via raw CSS (ikke Tailwind translate-utility) så slide-up-
   transformen ikke konflikter med Tailwind's transform-variabler. */
.saving-root {
  transform: translateX(-50%);
}

.slide-up-enter-active,
.slide-up-leave-active {
  transition:
    transform 0.28s ease,
    opacity 0.28s ease;
}
.slide-up-enter-from,
.slide-up-leave-to {
  transform: translate(-50%, 1.5rem);
  opacity: 0;
}

/* Determinate fill: blød overgang når den estimerede procent stiger. */
.progress-fill {
  transition:
    width 0.22s ease-out,
    background-color 0.2s ease;
}

/* Lille spinner i ink-farve. */
.spinner {
  width: 0.85rem;
  height: 0.85rem;
  flex: none;
  border-radius: 9999px;
  border: 2px solid rgb(var(--border));
  border-top-color: rgb(var(--text));
  animation: spin 0.7s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
